import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { Modal } from '../ui/Modal';
import { TrashIcon, PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine, PieChart, Pie, Cell,
  LineChart, Line, Legend,
} from 'recharts';
import toast from 'react-hot-toast';
import apiClient from '../../utils/api';

import { RangeSelector, useTimeRange } from './TimeRange';

const MEAL_COLORS = {
  breakfast: '#FFB800',
  lunch: '#22c55e',
  dinner: '#f97316',
  snack: '#C77D6A',
};

const MACRO_COLORS = {
  protein: '#ef4444',
  carbs: '#10B981',
  fat: '#eab308',
};

const getMealTypeColor = (mealType) => {
  const colors = { breakfast: 'primary', lunch: 'secondary', dinner: 'success', snack: 'warning' };
  return colors[mealType] || 'default';
};

// A food entry's calories/macros are AI-estimated (not a measured fact) when it
// came from the parser/voice logger and hasn't been edited or confirmed. Honesty
// surface: flag these so an estimate is never shown as if the user had confirmed it.
const isEstimated = (entry) =>
  (entry.source || '').startsWith('ai') && !entry.data?.confirmed;

const NutritionDataView = () => {
  const [allEntries, setAllEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const { inRange, spanDays, selectorProps } = useTimeRange();
  const [calorieTarget, setCalorieTarget] = useState(null);
  const [proteinTarget, setProteinTarget] = useState(null);
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetInput, setTargetInput] = useState('');

  // Log table filters (independent of chart range)
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Delete modal
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingEntryId, setDeletingEntryId] = useState(null);

  // Edit modal
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editForm, setEditForm] = useState({});

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/api/health/entries', {
        // nutrition.food only — the 'nutrition' prefix would also pull in
        // supplements and hydration (which have their own tabs) and pollute the
        // food log and macro averages.
        params: { category: 'nutrition.food', limit: 1000 },
      });
      setAllEntries(response.data);
    } catch (error) {
      console.error('Error fetching nutrition entries:', error);
      toast.error('Failed to fetch nutrition entries');
    } finally {
      setLoading(false);
    }
  }, []);

  // Calorie & protein targets both live in the unified MetricTarget store — one
  // source of truth shared with the You page and coaching. The Data page keeps
  // its simple single-number UX: calories map to the band's ceiling
  // (ultimate_high), and only a user-set (custom) value shows as "your target".
  // Protein is shown as its floor.
  const fetchTargets = useCallback(async () => {
    try {
      const res = await apiClient.get('/api/twin/targets');
      const list = res.data || [];
      const cal = list.find((t) => t.metric_key === 'calories_in');
      setCalorieTarget(
        cal && cal.ultimate_is_custom && cal.ultimate_high != null ? cal.ultimate_high : null
      );
      const p = list.find((t) => t.metric_key === 'protein_g');
      if (p) setProteinTarget(p.ultimate_low ?? p.seed_low ?? null);
    } catch {
      // Targets are optional context here — never block the nutrition view.
    }
  }, []);

  useEffect(() => {
    fetchEntries();
    fetchTargets();
  }, [fetchEntries, fetchTargets]);

  // Filter entries for charts by time range
  const chartEntries = useMemo(() => {
    if (!allEntries.length) return [];
    return allEntries.filter(e => inRange(e.entry_date));
  }, [allEntries, inRange]);

  // Filter entries for the log table (independent date filters)
  const logEntries = useMemo(() => {
    return allEntries.filter(e => {
      if (startDate && e.entry_date < startDate) return false;
      if (endDate && e.entry_date > endDate) return false;
      return true;
    });
  }, [allEntries, startDate, endDate]);

  // Aggregate data by date for charts
  const dailyData = useMemo(() => {
    const byDate = {};
    chartEntries.forEach(entry => {
      const date = entry.entry_date;
      if (!byDate[date]) {
        byDate[date] = { date, breakfast: 0, lunch: 0, dinner: 0, snack: 0, protein: 0, carbs: 0, fat: 0, totalCal: 0 };
      }
      const meal = entry.data?.meal_type || 'snack';
      const cal = entry.calories || 0;
      const key = ['breakfast', 'lunch', 'dinner', 'snack'].includes(meal) ? meal : 'snack';
      byDate[date][key] += cal;
      byDate[date].totalCal += cal;
      byDate[date].protein += entry.data?.protein || 0;
      byDate[date].carbs += entry.data?.carbs || 0;
      byDate[date].fat += entry.data?.fat || 0;
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [chartEntries]);

  // Summary stats for the selected range
  const stats = useMemo(() => {
    if (!dailyData.length) return null;
    const days = dailyData.length;
    const totalCal = dailyData.reduce((s, d) => s + d.totalCal, 0);
    const totalProtein = dailyData.reduce((s, d) => s + d.protein, 0);
    const totalCarbs = dailyData.reduce((s, d) => s + d.carbs, 0);
    const totalFat = dailyData.reduce((s, d) => s + d.fat, 0);
    return {
      avgCalories: Math.round(totalCal / days),
      avgProtein: Math.round(totalProtein / days),
      avgCarbs: Math.round(totalCarbs / days),
      avgFat: Math.round(totalFat / days),
      totalProtein,
      totalCarbs,
      totalFat,
      days,
    };
  }, [dailyData]);

  // Donut chart data
  const donutData = useMemo(() => {
    if (!stats || (stats.totalProtein === 0 && stats.totalCarbs === 0 && stats.totalFat === 0)) return [];
    const total = stats.totalProtein + stats.totalCarbs + stats.totalFat;
    return [
      { name: 'Protein', value: stats.totalProtein, pct: Math.round((stats.totalProtein / total) * 100), color: MACRO_COLORS.protein },
      { name: 'Carbs', value: stats.totalCarbs, pct: Math.round((stats.totalCarbs / total) * 100), color: MACRO_COLORS.carbs },
      { name: 'Fat', value: stats.totalFat, pct: Math.round((stats.totalFat / total) * 100), color: MACRO_COLORS.fat },
    ];
  }, [stats]);

  // Handlers
  const handleDeleteClick = (entryId) => {
    setDeletingEntryId(entryId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingEntryId) return;
    try {
      await apiClient.delete(`/api/health/entries/${deletingEntryId}`);
      toast.success('Entry deleted');
      setDeleteDialogOpen(false);
      setDeletingEntryId(null);
      fetchEntries();
    } catch (error) {
      console.error('Error deleting entry:', error);
      toast.error('Failed to delete entry');
    }
  };

  // Quick-confirm an AI estimate without opening the editor — accepts the macros
  // as-is and stamps data.confirmed so the "AI est." flag clears.
  const confirmEntry = async (entry) => {
    try {
      await apiClient.put(`/api/health/entries/${entry.id}`, {
        data: { ...(entry.data || {}), confirmed: true },
      });
      toast.success('Macros confirmed');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to confirm entry');
    }
  };

  const handleEditClick = (entry) => {
    const data = entry.data || {};
    setEditingEntry(entry);
    setEditForm({
      food_name: data.food_name || '',
      meal_type: data.meal_type || 'snack',
      quantity: data.quantity || '',
      unit: data.unit || '',
      calories: entry.calories || '',
      protein: data.protein ?? '',
      carbs: data.carbs ?? '',
      fat: data.fat ?? '',
      fiber: data.fiber ?? '',
      entry_date: entry.entry_date || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!editingEntry) return;
    try {
      const updatedData = {
        ...editingEntry.data,
        confirmed: true, // editing a value is an implicit confirmation
        food_name: editForm.food_name,
        meal_type: editForm.meal_type,
        quantity: editForm.quantity ? parseFloat(editForm.quantity) : null,
        unit: editForm.unit,
        protein: editForm.protein !== '' ? parseFloat(editForm.protein) : null,
        carbs: editForm.carbs !== '' ? parseFloat(editForm.carbs) : null,
        fat: editForm.fat !== '' ? parseFloat(editForm.fat) : null,
        fiber: editForm.fiber !== '' ? parseFloat(editForm.fiber) : null,
      };
      await apiClient.put(`/api/health/entries/${editingEntry.id}`, {
        entry_date: editForm.entry_date,
        calories: editForm.calories !== '' ? parseFloat(editForm.calories) : null,
        data: updatedData,
      });
      toast.success('Entry updated');
      setEditDialogOpen(false);
      setEditingEntry(null);
      fetchEntries();
    } catch (error) {
      console.error('Error updating entry:', error);
      toast.error('Failed to update entry');
    }
  };

  const saveCalorieTarget = async () => {
    const val = targetInput ? parseFloat(targetInput) : null;
    try {
      if (val == null) {
        // Clearing → restore the seeded default (no custom ceiling).
        await apiClient.post('/api/twin/targets/calories_in/reset');
      } else {
        // Single number = the ceiling; open-ended below (no false "eat at least" floor).
        await apiClient.patch('/api/twin/targets/calories_in', {
          ultimate_low: null,
          ultimate_high: val,
        });
      }
      setCalorieTarget(val);
      setEditingTarget(false);
      toast.success('Calorie target updated');
    } catch (error) {
      toast.error('Failed to update calorie target');
    }
  };

  const startEditTarget = () => {
    setTargetInput(calorieTarget != null ? String(calorieTarget) : '');
    setEditingTarget(true);
  };

  // Chart formatting
  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    const days = spanDays;
    if (days && days <= 90) {
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };

  const tickInterval = Math.max(0, Math.ceil(dailyData.length / 8) - 1);

  // Custom tooltips
  const CalorieTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const dateLabel = new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
    return (
      <div className="bg-raised border border-line rounded-lg p-3 text-sm">
        <p className="font-medium text-fg mb-2">{dateLabel}</p>
        <div className="space-y-1">
          {d.breakfast > 0 && <p style={{ color: MEAL_COLORS.breakfast }}>Breakfast: <span className="font-medium">{d.breakfast} kcal</span></p>}
          {d.lunch > 0 && <p style={{ color: MEAL_COLORS.lunch }}>Lunch: <span className="font-medium">{d.lunch} kcal</span></p>}
          {d.dinner > 0 && <p style={{ color: MEAL_COLORS.dinner }}>Dinner: <span className="font-medium">{d.dinner} kcal</span></p>}
          {d.snack > 0 && <p style={{ color: MEAL_COLORS.snack }}>Snack: <span className="font-medium">{d.snack} kcal</span></p>}
          <p className="border-t border-line  pt-1 mt-1 font-medium text-fg">
            Total: {d.totalCal} kcal
          </p>
        </div>
      </div>
    );
  };

  const MacroTrendTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    const dateLabel = new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric',
    });
    return (
      <div className="bg-raised border border-line rounded-lg p-3 text-sm">
        <p className="font-medium text-fg mb-2">{dateLabel}</p>
        <div className="space-y-1">
          <p style={{ color: MACRO_COLORS.protein }}>Protein: <span className="font-medium">{Math.round(d.protein)}g</span></p>
          <p style={{ color: MACRO_COLORS.carbs }}>Carbs: <span className="font-medium">{Math.round(d.carbs)}g</span></p>
          <p style={{ color: MACRO_COLORS.fat }}>Fat: <span className="font-medium">{Math.round(d.fat)}g</span></p>
        </div>
      </div>
    );
  };

  const DonutTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    return (
      <div className="bg-raised border border-line rounded-lg p-3 text-sm">
        <p style={{ color: d.color }} className="font-medium">{d.name}: {d.pct}%</p>
        <p className="text-fg-2">{Math.round(d.value)}g total</p>
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-fg"></div>
      </div>
    );
  }

  // Empty state
  if (!allEntries.length) {
    return (
      <Card>
        <CardContent className="py-12">
          <p className="text-center text-fg-2">
            No nutrition entries found. Use the AI input above to log meals.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Range Selector */}
      <RangeSelector
        {...selectorProps}
        right={(
          <span className="shrink-0 text-sm text-fg-2">
            {stats?.days || 0} days with data
          </span>
        )}
      />

      {/* Summary Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-fg-2">Avg Daily Calories</p>
            <p className="text-2xl font-medium text-fg">
              {stats?.avgCalories ?? '--'}
            </p>
            {calorieTarget && stats?.avgCalories ? (
              <p className={`text-sm mt-1 ${stats.avgCalories <= calorieTarget ? 'text-good-strong dark:text-good' : 'text-red-400'}`}>
                {stats.avgCalories <= calorieTarget ? 'Under' : 'Over'} target ({calorieTarget})
              </p>
            ) : (
              <p className="text-sm text-fg-2 mt-1">kcal/day</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-fg-2">Avg Protein</p>
            <p className="text-2xl font-medium" style={{ color: MACRO_COLORS.protein }}>
              {stats?.avgProtein ?? '--'}
            </p>
            {proteinTarget && stats?.avgProtein ? (
              <p className={`text-sm mt-1 ${stats.avgProtein >= proteinTarget ? 'text-good-strong dark:text-good' : 'text-fg-3'}`}>
                {stats.avgProtein >= proteinTarget ? 'At/above' : 'Below'} target ({proteinTarget}g)
              </p>
            ) : (
              <p className="text-sm text-fg-2 mt-1">g/day</p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-fg-2">Avg Carbs</p>
            <p className="text-2xl font-medium" style={{ color: MACRO_COLORS.carbs }}>
              {stats?.avgCarbs ?? '--'}
            </p>
            <p className="text-sm text-fg-2 mt-1">g/day</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-fg-2">Avg Fat</p>
            <p className="text-2xl font-medium" style={{ color: MACRO_COLORS.fat }}>
              {stats?.avgFat ?? '--'}
            </p>
            <p className="text-sm text-fg-2 mt-1">g/day</p>
          </CardContent>
        </Card>
      </div>

      {/* Calorie Target Inline Editor */}
      <div className="flex items-center gap-3">
        <span className="text-sm text-fg-2">Daily Calorie Target:</span>
        {editingTarget ? (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={targetInput}
              onChange={(e) => setTargetInput(e.target.value)}
              placeholder="e.g. 2000"
              className="w-32"
              min="0"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveCalorieTarget();
                if (e.key === 'Escape') setEditingTarget(false);
              }}
            />
            <button onClick={saveCalorieTarget} className="p-1 text-good-strong hover:text-green-700">
              <CheckIcon className="w-5 h-5" />
            </button>
            <button onClick={() => setEditingTarget(false)} className="p-1 text-fg-3 hover:text-fg">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <button
            onClick={startEditTarget}
            className="flex items-center gap-1.5 text-sm font-medium text-fg-2 hover:text-fg transition-colors"
          >
            {calorieTarget != null ? `${calorieTarget} kcal` : <span className="text-fg-3 italic">Set target</span>}
            <PencilIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Daily Calorie Bar Chart */}
      {dailyData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Daily Calories</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={dailyData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
                <XAxis
                  dataKey="date"
                  tickFormatter={formatDate}
                  interval={tickInterval}
                  tick={{ fontSize: 14, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                />
                <YAxis
                  tick={{ fontSize: 14, fill: '#9ca3af' }}
                  axisLine={{ stroke: '#4b5563' }}
                  tickLine={{ stroke: '#4b5563' }}
                  width={50}
                  label={{ value: 'kcal', angle: -90, position: 'insideLeft', style: { fill: '#9ca3af', fontSize: 14 } }}
                />
                <Tooltip content={<CalorieTooltip />} />
                <Legend
                  formatter={(value) => {
                    const map = { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', snack: 'Snack' };
                    return map[value] || value;
                  }}
                  wrapperStyle={{ fontSize: 14 }}
                />
                {calorieTarget && (
                  <ReferenceLine
                    y={calorieTarget}
                    stroke="#ef4444"
                    strokeDasharray="5 3"
                    label={{ value: `Target ${calorieTarget}`, position: 'insideTopLeft', fill: '#ef4444', fontSize: 14 }}
                  />
                )}
                <Bar dataKey="breakfast" stackId="cal" fill={MEAL_COLORS.breakfast} name="breakfast" />
                <Bar dataKey="lunch" stackId="cal" fill={MEAL_COLORS.lunch} name="lunch" />
                <Bar dataKey="dinner" stackId="cal" fill={MEAL_COLORS.dinner} name="dinner" />
                <Bar dataKey="snack" stackId="cal" fill={MEAL_COLORS.snack} name="snack" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Macro Donut + Macro Trend Lines (two-col grid) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Macro Donut */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Macro Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {donutData.length > 0 ? (
              <div className="flex flex-col items-center">
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie
                      data={donutData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={3}
                      dataKey="value"
                    >
                      {donutData.map((entry, i) => (
                        <Cell key={i} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={<DonutTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex gap-6 text-sm mt-2">
                  {donutData.map(d => (
                    <div key={d.name} className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full inline-block" style={{ backgroundColor: d.color }} />
                      <span className="text-fg-3">{d.name} {d.pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-center text-fg-2 py-8">No macro data available</p>
            )}
          </CardContent>
        </Card>

        {/* Macro Trend Lines */}
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Macro Trends</CardTitle>
          </CardHeader>
          <CardContent>
            {dailyData.length > 0 && (dailyData.some(d => d.protein > 0 || d.carbs > 0 || d.fat > 0)) ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={dailyData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#6b7280" opacity={0.15} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDate}
                    interval={tickInterval}
                    tick={{ fontSize: 14, fill: '#9ca3af' }}
                    axisLine={{ stroke: '#4b5563' }}
                    tickLine={{ stroke: '#4b5563' }}
                  />
                  <YAxis
                    tick={{ fontSize: 14, fill: '#9ca3af' }}
                    axisLine={{ stroke: '#4b5563' }}
                    tickLine={{ stroke: '#4b5563' }}
                    width={40}
                    label={{ value: 'g', angle: -90, position: 'insideLeft', style: { fill: '#9ca3af', fontSize: 14 } }}
                  />
                  <Tooltip content={<MacroTrendTooltip />} />
                  <Legend
                    formatter={(value) => {
                      const map = { protein: 'Protein', carbs: 'Carbs', fat: 'Fat' };
                      return map[value] || value;
                    }}
                    wrapperStyle={{ fontSize: 14 }}
                  />
                  <Line dataKey="protein" stroke={MACRO_COLORS.protein} strokeWidth={2} dot={dailyData.length <= 30 ? { r: 2, fill: MACRO_COLORS.protein } : false} type="monotone" connectNulls />
                  <Line dataKey="carbs" stroke={MACRO_COLORS.carbs} strokeWidth={2} dot={dailyData.length <= 30 ? { r: 2, fill: MACRO_COLORS.carbs } : false} type="monotone" connectNulls />
                  <Line dataKey="fat" stroke={MACRO_COLORS.fat} strokeWidth={2} dot={dailyData.length <= 30 ? { r: 2, fill: MACRO_COLORS.fat } : false} type="monotone" connectNulls />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-center text-fg-2 py-8">No macro data available</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Log Table Section */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-fg">Food Log</h3>

        {/* Date Filters */}
        <div className="flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-fg-2 mb-1">
              Start Date
            </label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-fg-2 mb-1">
              End Date
            </label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          {(startDate || endDate) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setStartDate('');
                setEndDate('');
              }}
            >
              Clear Filters
            </Button>
          )}
        </div>

        {/* Table */}
        {logEntries.length === 0 ? (
          <p className="text-fg-2">
            No nutrition entries found for the selected period.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-line">
                  <th className="text-left py-3 px-4 font-medium text-fg">Date</th>
                  <th className="text-left py-3 px-4 font-medium text-fg">Meal</th>
                  <th className="text-left py-3 px-4 font-medium text-fg">Food</th>
                  <th className="text-left py-3 px-4 font-medium text-fg">Qty</th>
                  <th className="text-left py-3 px-4 font-medium text-fg">Calories</th>
                  <th className="text-left py-3 px-4 font-medium text-fg">Protein</th>
                  <th className="text-left py-3 px-4 font-medium text-fg">Carbs</th>
                  <th className="text-left py-3 px-4 font-medium text-fg">Fat</th>
                  <th className="text-left py-3 px-4 font-medium text-fg">Fiber</th>
                  <th className="text-left py-3 px-4 font-medium text-fg">Actions</th>
                </tr>
              </thead>
              <tbody>
                {logEntries.map((entry) => {
                  const data = entry.data || {};
                  const mealType = data.meal_type || '-';
                  const foodName = data.food_name || entry.entry_type || '-';
                  const qty = data.quantity ? `${data.quantity} ${data.unit || ''}` : '-';

                  return (
                    <tr key={entry.id} className="border-b border-line hover:bg-elev">
                      <td className="py-3 px-4 text-fg">{new Date(entry.entry_date).toLocaleDateString()}</td>
                      <td className="py-3 px-4">
                        <Badge variant={getMealTypeColor(mealType)}>
                          {mealType}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-fg">
                        <div className="flex items-center gap-2">
                          <span>{foodName}</span>
                          {isEstimated(entry) && (
                            <button
                              type="button"
                              onClick={() => confirmEntry(entry)}
                              title="Calories & macros estimated by AI — click to confirm them"
                              className="shrink-0 text-sm font-medium px-2 py-0.5 rounded-full bg-elev hover:bg-raised text-fg-2   hover:bg-raised transition-colors"
                            >
                              AI est.
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-fg">{qty}</td>
                      <td className="py-3 px-4 text-fg">{entry.calories || '-'}</td>
                      <td className="py-3 px-4 text-fg">{data.protein || '-'}</td>
                      <td className="py-3 px-4 text-fg">{data.carbs || '-'}</td>
                      <td className="py-3 px-4 text-fg">{data.fat || '-'}</td>
                      <td className="py-3 px-4 text-fg">{data.fiber || '-'}</td>
                      <td className="py-3 px-4">
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEditClick(entry)}
                            className="p-2 text-fg-3 hover:text-fg"
                            aria-label={`Edit entry for ${foodName}`}
                          >
                            <PencilIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteClick(entry.id)}
                            className="p-2 text-off-strong hover:text-red-700"
                            aria-label={`Delete entry for ${foodName}`}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeletingEntryId(null);
        }}
        title="Delete Entry"
      >
        <div className="space-y-4">
          <p className="text-fg-2">
            Are you sure you want to delete this entry? This action cannot be undone.
          </p>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeletingEntryId(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Entry Modal */}
      <Modal
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingEntry(null);
        }}
        title="Edit Entry"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-1">Date</label>
              <Input
                type="date"
                value={editForm.entry_date || ''}
                onChange={(e) => setEditForm(f => ({ ...f, entry_date: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-1">Meal Type</label>
              <select
                value={editForm.meal_type || 'snack'}
                onChange={(e) => setEditForm(f => ({ ...f, meal_type: e.target.value }))}
                className="flex h-10 w-full rounded-md border border-line-strong bg-elev px-3 py-2 text-sm  "
              >
                <option value="breakfast">Breakfast</option>
                <option value="lunch">Lunch</option>
                <option value="dinner">Dinner</option>
                <option value="snack">Snack</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-fg-2 mb-1">Food Name</label>
            <Input
              value={editForm.food_name || ''}
              onChange={(e) => setEditForm(f => ({ ...f, food_name: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-1">Quantity</label>
              <Input
                type="number"
                value={editForm.quantity ?? ''}
                onChange={(e) => setEditForm(f => ({ ...f, quantity: e.target.value }))}
                min="0"
                step="0.1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-1">Unit</label>
              <Input
                value={editForm.unit || ''}
                onChange={(e) => setEditForm(f => ({ ...f, unit: e.target.value }))}
                placeholder="e.g. serving, g, oz"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-1">Calories</label>
              <Input
                type="number"
                value={editForm.calories ?? ''}
                onChange={(e) => setEditForm(f => ({ ...f, calories: e.target.value }))}
                min="0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-1">Protein (g)</label>
              <Input
                type="number"
                value={editForm.protein ?? ''}
                onChange={(e) => setEditForm(f => ({ ...f, protein: e.target.value }))}
                min="0"
                step="0.1"
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-1">Carbs (g)</label>
              <Input
                type="number"
                value={editForm.carbs ?? ''}
                onChange={(e) => setEditForm(f => ({ ...f, carbs: e.target.value }))}
                min="0"
                step="0.1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-1">Fat (g)</label>
              <Input
                type="number"
                value={editForm.fat ?? ''}
                onChange={(e) => setEditForm(f => ({ ...f, fat: e.target.value }))}
                min="0"
                step="0.1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-fg-2 mb-1">Fiber (g)</label>
              <Input
                type="number"
                value={editForm.fiber ?? ''}
                onChange={(e) => setEditForm(f => ({ ...f, fiber: e.target.value }))}
                min="0"
                step="0.1"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2 pt-2">
            <Button
              variant="outline"
              onClick={() => {
                setEditDialogOpen(false);
                setEditingEntry(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleEditSave}>
              Save Changes
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default NutritionDataView;
